// Hook de sincronización offline para capturas de muestreo.
//
// - Expone `online`, `pendientes` (conteo) y `sincronizando`.
// - Sincroniza AUTOMÁTICAMENTE al volver la conexión (evento 'online') y al montar.
// - `sincronizar()` también se puede llamar a mano.
// - Cada pendiente se reenvía al backend; si responde ok se quita de la cola.

import { useCallback, useEffect, useRef, useState } from 'react';
import { buildApiUrl } from '../utils/apiUrl';
import { listarPendientes, contarPendientes, quitar, marcarIntento } from '../utils/offlineQueue';

export function useOfflineSync() {
  const [online, setOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendientes, setPendientes] = useState(0);
  const [sincronizando, setSincronizando] = useState(false);
  const sincRef = useRef(false); // evita sync concurrente

  const refrescarConteo = useCallback(async () => {
    try { setPendientes(await contarPendientes()); } catch { /* idb no disponible */ }
  }, []);

  const sincronizar = useCallback(async (): Promise<{ ok: number; fail: number }> => {
    if (sincRef.current || !navigator.onLine) return { ok: 0, fail: 0 };
    sincRef.current = true;
    setSincronizando(true);
    let ok = 0, fail = 0;
    try {
      const items = await listarPendientes();
      for (const it of items) {
        try {
          const res = await fetch(
            buildApiUrl(`/aplicaciones/labores/programa/${it.muestreoId}/captura?empresa_id=${it.empresaId}`),
            { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(it.payload) }
          );
          const j = await res.json().catch(() => ({ ok: false }));
          if (j.ok) { await quitar(it.client_uuid); ok++; }
          else { await marcarIntento(it.client_uuid); fail++; }
        } catch {
          await marcarIntento(it.client_uuid); fail++;
        }
      }
    } finally {
      sincRef.current = false;
      setSincronizando(false);
      await refrescarConteo();
    }
    return { ok, fail };
  }, [refrescarConteo]);

  useEffect(() => {
    refrescarConteo();
    const onOnline = () => { setOnline(true); sincronizar(); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    // intento inicial si arrancamos con señal y hay pendientes
    if (navigator.onLine) sincronizar();
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { online, pendientes, sincronizando, sincronizar, refrescarConteo };
}
